import React, { useMemo, useState } from 'react'
import { useSourceView } from './UseSourceView.ts'
import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { useTransformer } from '../Transformer/UseTransformer.tsx'

type OverlapMetric = 'jaccard' | 'weightedJaccard'

type OverlapHeatmapParams = {
  title?: string
  metric?: OverlapMetric
  maxBuckets?: number
  minMagnitude?: number
}

const EMPTY_PATH: string[] = []

type BucketSummary = {
  key: string
  label: string
  present: Set<string>
  mags: Map<string, number>
  valueCount: number
  distinctTaxa: number
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 1
  let inter = 0
  for (const x of a) if (b.has(x)) inter++
  const union = a.size + b.size - inter
  return union === 0 ? 0 : inter / union
}

function weightedJaccard(a: Map<string, number>, b: Map<string, number>): number {
  const keys = new Set<string>()
  for (const k of a.keys()) keys.add(k)
  for (const k of b.keys()) keys.add(k)

  let sumMin = 0
  let sumMax = 0
  for (const k of keys) {
    const av = a.get(k) ?? 0
    const bv = b.get(k) ?? 0
    sumMin += Math.min(av, bv)
    sumMax += Math.max(av, bv)
  }
  return sumMax === 0 ? 0 : sumMin / sumMax
}

export default function OverlapHeatmapSink({
  sourceId,
  config
}: {
  sourceId: SourceId
  sinkId: SinkId
  config: SinkConfig<OverlapHeatmapParams>
}) {
  const { snapshot, shared, status } = useSourceView(sourceId)

  const selectedPath = shared?.selectedPath ?? EMPTY_PATH
  const gradeThreshold = shared?.gradeThreshold ?? 0

  const bucketsRes = useTransformer(sourceId, undefined, 'kronaFilteredBuckets', {
    selectedPath,
    gradeThreshold
  })

  const metric: OverlapMetric = config.params?.metric ?? 'weightedJaccard'
  const maxBuckets = config.params?.maxBuckets ?? 20
  const minMagnitude = config.params?.minMagnitude ?? 0

  const [picked, setPicked] = useState<{ a: string; b: string; value: number } | null>(null)

  const { buckets, matrix, anyEmpty } = useMemo(() => {
    if (bucketsRes.status !== 'ready') return { buckets: [] as BucketSummary[], matrix: [] as number[][], anyEmpty: false }

    const depth = selectedPath.length

    const summaries: BucketSummary[] = bucketsRes.value.slice(0, maxBuckets).map((b) => {
      const present = new Set<string>()
      const mags = new Map<string, number>()
      let count = 0

      for (const v of b.values) {
        const tax = v.taxa[depth] ?? '(n/a)'
        const mag = v.magnitude ?? 0
        if (mag < minMagnitude) continue

        count++
        present.add(tax)
        mags.set(tax, (mags.get(tax) ?? 0) + mag)
      }

      return {
        key: b.bucketKey,
        label: b.bucketLabel,
        present,
        mags,
        valueCount: count,
        distinctTaxa: present.size
      }
    })

    const n = summaries.length
    const mat: number[][] = Array.from({ length: n }, () => Array(n).fill(0))

    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        mat[i]![j] =
          metric === 'jaccard'
            ? jaccard(summaries[i]!.present, summaries[j]!.present)
            : weightedJaccard(summaries[i]!.mags, summaries[j]!.mags)
      }
    }

    const anyEmpty = summaries.some((s) => s.valueCount === 0 || s.distinctTaxa === 0)
    return { buckets: summaries, matrix: mat, anyEmpty }
  }, [bucketsRes, selectedPath.length, metric, maxBuckets, minMagnitude])

  if (!snapshot) {
    return (
      <div className="text-sm opacity-70">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  if (bucketsRes.status === 'idle' || bucketsRes.status === 'loading') {
    return (
      <div className="text-sm opacity-70">Computing… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}</div>
    )
  }

  if (buckets.length === 0) {
    return <div className="text-sm opacity-70">No buckets in view.</div>
  }

  const toShade = (x: number) => {
    const v = Math.max(0, Math.min(1, x))
    const hue = 210
    const sat = 70
    const light = 96 - v * 55
    return `hsl(${hue} ${sat}% ${light}%)`
  }

  const textColorFor = (x: number) => (x > 0.55 ? 'white' : 'black')

  const n = buckets.length
  const showCellText = n <= 14

  const headerH = n <= 6 ? 200 : n <= 10 ? 160 : 140
  const labelColW = 260

  const cellPx = 68
  const gap = 10

  const legendStops = [0, 0.25, 0.5, 0.75, 1]

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="text-xs opacity-60 pl-2">
        Level: <span className="font-mono">{selectedPath.join(' › ') || 'Root'}</span>
        <span className="ml-2 opacity-50">(n={n})</span>
        <span className="pl-5">
          Metric: <span className="font-mono">{metric}</span>
        </span>
      </div>

      <div className="flex items-center gap-2 text-[11px] opacity-70 pl-2">
        <span className="opacity-60">low</span>
        <div className="flex items-center gap-1">
          {legendStops.map((v) => (
            <div key={v} className="h-3 w-10 rounded border border-black/10" title={String(v)} style={{ backgroundColor: toShade(v) }} />
          ))}
        </div>
        <span className="opacity-60">high</span>

        {anyEmpty && (
          <span className="ml-3 text-[11px] opacity-60">(Some buckets have 0 values at this level — check minMagnitude / zoom depth)</span>
        )}
      </div>

      {/* Single scroll viewport. Row labels, column headers, and cells all share ONE (n+1)×(n+1)
          grid, so they can never desync. The label column / header row stay put via `position:
          sticky` instead of being separate independently-scrolling panes. */}
      <div className="flex-1 overflow-auto rounded border border-black/10 bg-base-100 p-3">
        <div
          className="grid w-max"
          style={{
            gridTemplateColumns: `${labelColW}px repeat(${n}, ${cellPx}px)`,
            gridTemplateRows: `${headerH}px repeat(${n}, ${cellPx}px)`,
            gap
          }}
        >
          {/* Corner (sticky to both edges so it wins the top-left intersection) */}
          <div className="sticky left-0 top-0 z-30 bg-base-100 text-xs opacity-60 flex items-end pb-2">
            Buckets
          </div>

          {/* Column headers — sticky to the top, scroll horizontally with the cells */}
          {buckets.map((b) => (
            <div key={`col-${b.key}`} className="sticky top-0 z-20 bg-base-100 flex justify-center" title={b.label}>
              <div
                className="text-xs opacity-70"
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  maxHeight: headerH - 8,
                  maskImage: 'linear-gradient(to bottom, black 85%, transparent)',
                  WebkitMaskImage: 'linear-gradient(to bottom, black 85%, transparent)'
                }}
              >
                {b.label}
              </div>
            </div>
          ))}

          {/* One grid row per bucket: sticky row label, then the cells */}
          {buckets.map((rowB, i) => (
            <React.Fragment key={`row-${rowB.key}`}>
              <div
                className="sticky left-0 z-10 bg-base-100 flex items-center text-xs opacity-70 truncate pr-2"
                title={`${rowB.label}\nvalues=${rowB.valueCount}, taxa=${rowB.distinctTaxa}`}
              >
                {rowB.label}
              </div>

              {buckets.map((colB, j) => {
                const v = matrix[i]![j]!
                const isDiag = i === j
                const fill = toShade(v)

                return (
                  <button
                    key={`${rowB.key}-${colB.key}`}
                    className="rounded border border-black/10 flex items-center justify-center font-mono"
                    style={{
                      width: cellPx,
                      height: cellPx,
                      backgroundColor: fill,
                      borderWidth: isDiag ? 2 : 1
                    }}
                    title={`${rowB.label} × ${colB.label}: ${v.toFixed(3)}`}
                    onClick={() => setPicked({ a: rowB.label, b: colB.label, value: v })}
                  >
                    <span className="text-[10px]" style={{ color: textColorFor(v) }}>
                      {showCellText ? v.toFixed(2) : ''}
                    </span>
                  </button>
                )
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {picked && (
        <div className="text-xs opacity-70">
          {picked.a} × {picked.b}: <span className="font-mono">{picked.value.toFixed(4)}</span>
        </div>
      )}
    </div>
  )
}
